import React, { useEffect, useRef, useState } from 'react';
import { Clipboard, Image as ImageIcon, Mic, StopCircle, UploadCloud, X } from 'lucide-react';

function audioMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  return '';
}

function extensionForMime(mimeType = '') {
  if (mimeType.includes('ogg') || mimeType.includes('opus')) return 'ogg';
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return 'mp3';
  if (mimeType.includes('mp4')) return 'm4a';
  return 'webm';
}

function fileLabel(file) {
  if (!file) return '';
  const sizeKb = file.size ? Math.max(1, Math.round(file.size / 1024)) : null;
  return [file.name, sizeKb ? `${sizeKb} KB` : null].filter(Boolean).join(' - ');
}

export default function MediaMessageControls({
  value,
  onChange,
  disabled = false,
  compact = false,
  allowAudio = true,
  allowImages = true,
}) {
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => () => {
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    streamRef.current?.getTracks?.().forEach(track => track.stop());
  }, [value?.previewUrl]);

  useEffect(() => {
    if (!isImageModalOpen) return undefined;
    const handlePaste = event => {
      const file = imageFileFromClipboard(event.clipboardData);
      if (!file) return;
      event.preventDefault();
      selectImageFile(file);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isImageModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  function setMedia(nextMedia) {
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange?.(nextMedia);
  }

  function imageFileFromClipboard(clipboardData) {
    const items = Array.from(clipboardData?.items || []);
    const imageItem = items.find(item => item.type?.startsWith('image/'));
    return imageItem?.getAsFile?.() || null;
  }

  function selectImageFile(file) {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setError('Selecciona, arrastra o pega una imagen valida.');
      return;
    }
    setError(null);
    setMedia({
      type: 'image',
      file,
      previewUrl: URL.createObjectURL(file),
      label: fileLabel(file),
    });
    setIsImageModalOpen(false);
    setIsDragging(false);
  }

  function handleImageSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    selectImageFile(file);
  }

  function handleDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    const file = Array.from(event.dataTransfer?.files || []).find(item => item.type?.startsWith('image/'));
    selectImageFile(file);
  }

  async function startRecording() {
    if (disabled || isRecording) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Tu navegador no permite grabar audio aqui.');
      return;
    }
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = audioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      recorder.ondataavailable = event => {
        if (event.data?.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const finalMimeType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: finalMimeType });
        const ext = extensionForMime(finalMimeType);
        const file = new File([blob], `fastmax-audio-${Date.now()}.${ext}`, { type: finalMimeType });
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        if (blob.size > 0) {
          setMedia({
            type: 'audio',
            file,
            previewUrl: URL.createObjectURL(blob),
            label: 'Audio grabado',
          });
        }
      };
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Audio recording error:', err);
      setError('No se pudo iniciar la grabacion de audio.');
      streamRef.current?.getTracks?.().forEach(track => track.stop());
      streamRef.current = null;
    }
  }

  function stopRecording() {
    if (!isRecording) return;
    setIsRecording(false);
    recorderRef.current?.stop();
  }

  function removeMedia() {
    setMedia(null);
    setError(null);
  }

  function renderCompactPreview() {
    if (!value) return null;
    return (
      <div className="flex min-w-0 max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
        {value.type === 'image' ? (
          <img src={value.previewUrl} alt="Vista previa" className="h-8 w-8 shrink-0 rounded-md object-cover" />
        ) : (
          <Mic className="h-4 w-4 shrink-0 text-slate-500" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-slate-700">
            {value.type === 'image' ? 'Imagen adjunta' : 'Audio adjunto'}
          </div>
          {value.type === 'audio' ? (
            <audio controls preload="metadata" src={value.previewUrl} className="h-8 w-[220px] max-w-full" />
          ) : (
            <div className="truncate text-[10px] font-semibold text-slate-500">{value.label}</div>
          )}
        </div>
        <button
          type="button"
          onClick={removeMedia}
          className="shrink-0 rounded-md p-1 text-slate-500 hover:bg-slate-200"
          aria-label="Quitar adjunto"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelected}
      />

      <div className="flex flex-wrap items-center gap-2">
        {allowImages && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setIsImageModalOpen(true);
            }}
            disabled={disabled || isRecording}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ImageIcon className="h-3.5 w-3.5" />
            Imagen
          </button>
        )}
        {allowAudio && (
          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 ${
              isRecording ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {isRecording ? <StopCircle className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            {isRecording ? 'Detener' : 'Audio'}
          </button>
        )}
        {compact && renderCompactPreview()}
        {isRecording && <span className="text-[11px] font-bold text-red-600">Grabando...</span>}
        {error && <span className="text-[11px] font-semibold text-red-600">{error}</span>}
      </div>

      {value && !compact && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="min-w-0 truncate font-bold text-slate-700">
              {value.type === 'image' ? 'Imagen adjunta' : 'Audio adjunto'} {value.label ? `- ${value.label}` : ''}
            </span>
            <button
              type="button"
              onClick={removeMedia}
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-200"
              aria-label="Quitar adjunto"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {value.type === 'image' ? (
            <img src={value.previewUrl} alt="Vista previa" className="max-h-40 rounded-lg object-contain" />
          ) : (
            <audio controls preload="metadata" src={value.previewUrl} className="w-full" />
          )}
        </div>
      )}

      {isImageModalOpen && (
        <div className="fixed inset-0 z-[3500] flex items-center justify-center bg-black/60 p-4" onClick={() => setIsImageModalOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={event => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-base font-extrabold text-slate-950">Enviar imagen</h3>
                <p className="text-xs font-semibold text-slate-500">Arrastra, pega o selecciona una imagen.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsImageModalOpen(false)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5">
              <div
                onDragOver={event => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`flex min-h-[230px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition ${
                  isDragging ? 'border-[#B1336E] bg-[#B1336E]/5' : 'border-slate-300 bg-slate-50'
                }`}
              >
                <div className="mb-3 rounded-2xl bg-white p-4 text-[#B1336E] shadow-sm">
                  <UploadCloud className="h-8 w-8" />
                </div>
                <p className="text-sm font-extrabold text-slate-900">Suelta la imagen aqui</p>
                <p className="mt-1 max-w-sm text-xs font-semibold text-slate-500">
                  Tambien puedes pegar una captura con Ctrl+V o elegir una imagen desde tu dispositivo.
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-extrabold text-white hover:bg-[#9d2d62]"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Seleccionar imagen
                  </button>
                  <div className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">
                    <Clipboard className="h-4 w-4" />
                    Ctrl+V para pegar
                  </div>
                </div>
              </div>
              {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700">{error}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
